import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Stack, Card, Divider} from 'views/components';
import {AiCall, Translate} from 'views/module';
import {CheckCircle} from 'views/icons';

const AGENT_ID = process.env.REACT_APP_ELEVENLAB_AGENT_ID;

const InterviewSection = (props) => {
  const {caregiverDataRef = {}, interviewQuestionsRef} = props;

  const questionStack = React.useRef([]);
  const categoryStack = React.useRef([]);

  const [connected, setConnected] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const onCallStart = () => {
    setConnected(true);
  };

  const onCallEnd = () => {
    setConnected(false);
    setDone(true);
  };

  React.useEffect(() => {
    questionStack.current = [interviewQuestionsRef.current];
    categoryStack.current = [questionStack.current.at(-1).pop()];
  }, []);

  // Interview states
  const getNextQuestion = () => {
    while (questionStack.current.length > 0) {
      const next = questionStack.current.at(-1).pop();
      if (Array.isArray(next)) { // if new category encountered
        categoryStack.current.push(next.pop());
        questionStack.current.push(next);
      } else if (next === undefined) { // if current category is exhausted
        questionStack.current.pop();
        categoryStack.current.pop();
      } else {
        return [categoryStack.current.at(-1), next];
      }
    }

    return '$INTERVIEW_COMPLETE';
  };

  const flattenCaregiverData = (caregiverData) => {
    const flatData = Object.entries(caregiverData).reduce((acc, [key, value]) => {
      if (Array.isArray(value)) {
        return {...acc, [key]: value};
      } else {
        return {...acc, ...value};
      }
    }, {});
    return flatData;
  };

  const clientTools = {
    getNextQuestion: getNextQuestion,
  };
  const dynamicVariables = {
    'secret__caregiver_data': JSON.stringify(flattenCaregiverData(caregiverDataRef.current || {})),
    'first_name': caregiverDataRef.current.contact.firstName || 'there',
  };

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverScreening.Interview.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverScreening.Interview.Subtitle'/>
      </Typography>

      <Stack spacing={3} alignItems="center">
        {/* Status Display */}
        <Card sx={{width: '100%', maxWidth: 500, p: 3}}>
          <Stack spacing={2} alignItems="center">

            <AiCall
              agentId={AGENT_ID}
              clientTools={clientTools}
              dynamicVariables={dynamicVariables}

              onCallStart={onCallStart}
              onCallEnd={onCallEnd}
            />
            <Divider sx={{width: '100%'}} />

            {/* Interview Instructions */}
            {
              !connected && !done && (
                <Box sx={{textAlign: 'center'}}>
                  <Typography variant="body1" gutterBottom>
                    <Translate expression='caregiverScreening.Interview.ReadyToStart.Title'/>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    <Translate expression='caregiverScreening.Interview.ReadyToStart.Instructions'/>
                  </Typography>
                </Box>
              )
            }

            {/* Active Call Controls */}
            {
              connected && (
                <Box sx={{textAlign: 'center'}}>
                  <Typography variant="body1" gutterBottom>
                    <Translate expression='caregiverScreening.Interview.InProgress.Title'/>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    <Translate expression='caregiverScreening.Interview.InProgress.Instructions'/>
                  </Typography>


                </Box>
              )
            }

            {/* Completed State */}
            {
              done && (
                <Box sx={{textAlign: 'center'}}>
                  <CheckCircle color="success" sx={{fontSize: 48, mb: 2}} />
                  <Typography variant="h6" gutterBottom color="success.main">
                    <Translate expression='caregiverScreening.Interview.Completed.Title'/>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Translate expression='caregiverScreening.Interview.Completed.Message'/>
                  </Typography>
                </Box>
              )
            }
          </Stack>
        </Card>

        {/* Interview Guidelines */}
        {
          !connected && !done && (
            <Card sx={{width: '100%', maxWidth: 500, p: 3, bgcolor: 'background.paper'}}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                <Translate expression='caregiverScreening.Interview.Guidelines.Title'/>
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverScreening.Interview.Guidelines.QuietEnvironment'/>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverScreening.Interview.Guidelines.SpeakClearly'/>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverScreening.Interview.Guidelines.BeHonest'/>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression='caregiverScreening.Interview.Guidelines.CoverTopics'/>
                </Typography>
              </Stack>
            </Card>
          )
        }
      </Stack>
    </Box>
  );
};

InterviewSection.propTypes = {
  caregiverDataRef: PropTypes.object,
  interviewQuestionsRef: PropTypes.object,
};

export default InterviewSection;
